/**
 * BarcodeExporter - Component to export generated barcodes to TXT file
 * Format: MODELO|GTIN|LOTE|VALIDADE|CODIGO_COMPLETO
 */
import React, { useState } from 'react';
import { Download, FileText, Trash2, Plus, Check, Copy, Printer } from 'lucide-react';
import { BarcodeModel } from './BarcodeModelSelector';
import { Product } from '../../services/productService';

export interface ExportableBarcode {
  model: BarcodeModel;
  product: Product;
  gtin: string;
  lote?: string;
  validade?: string;
  codigoCompleto: string;
}

interface BarcodeExporterProps {
  barcodes: ExportableBarcode[];
  onRemove?: (index: number) => void;
  onClear?: () => void;
}

export const BarcodeExporter: React.FC<BarcodeExporterProps> = ({
  barcodes,
  onRemove,
  onClear,
}) => {
  const [copied, setCopied] = useState(false);

  // T31 (SEGURANÇA): escape de HTML — os dados vêm do backend (nome de produto,
  // lote etc.) e são interpolados numa janela de impressão via document.write.
  // Sem escape, um produto com nome tipo `<img src=x onerror=...>` executa JS
  // (DOM/Stored XSS).
  const esc = (v: unknown): string =>
    String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const generateExportContent = (): string => {
    const header = 'MODELO|GTIN|LOTE|VALIDADE|CODIGO_COMPLETO';
    const lines = barcodes.map((b) =>
      `${b.model}|${b.gtin}|${b.lote || ''}|${formatDate(b.validade)}|${b.codigoCompleto}`
    );
    return [header, ...lines].join('\n');
  };

  const handleExportTXT = () => {
    const content = generateExportContent();
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `codigos_barras_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyAll = async () => {
    const content = generateExportContent();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Códigos de Barras - AuditLog</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #f59e0b; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f59e0b; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .code { font-family: monospace; font-size: 12px; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <h1>Códigos de Barras - AuditLog</h1>
        <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
        <table>
          <thead>
            <tr>
              <th>Modelo</th>
              <th>Produto</th>
              <th>GTIN</th>
              <th>Lote</th>
              <th>Validade</th>
              <th>Código Completo</th>
            </tr>
          </thead>
          <tbody>
            ${barcodes.map((b) => `
              <tr>
                <td>${esc(b.model)}</td>
                <td>${esc(b.product.nome)}</td>
                <td class="code">${esc(b.gtin)}</td>
                <td>${esc(b.lote || '-')}</td>
                <td>${b.validade ? esc(new Date(b.validade).toLocaleDateString('pt-BR')) : '-'}</td>
                <td class="code">${esc(b.codigoCompleto)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (barcodes.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-semibold text-gray-700 mb-2">Lista de Exportação Vazia</h3>
          <p className="text-sm text-gray-500 max-w-sm">
            Gere códigos de barras no simulador acima e eles aparecerão aqui para exportação.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-gray-800">
            Lista para Exportação ({barcodes.length} código{barcodes.length > 1 ? 's' : ''})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </button>
          <button
            onClick={handleExportTXT}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar TXT
          </button>
          {onClear && (
            <button
              onClick={onClear}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modelo</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">GTIN</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lote</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Validade</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código Completo</th>
              <th className="px-4 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {barcodes.map((barcode, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    barcode.model === 'A' ? 'bg-blue-100 text-blue-700' :
                    barcode.model === 'B' ? 'bg-green-100 text-green-700' :
                    barcode.model === 'C' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {barcode.model}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                    {barcode.product.nome}
                  </div>
                  <div className="text-xs text-gray-500">{barcode.product.codigo}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-sm text-gray-700">{barcode.gtin}</span>
                </td>
                <td className="px-4 py-3">
                  {barcode.lote ? (
                    <span className="font-mono text-sm text-gray-700">{barcode.lote}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {barcode.validade ? (
                    <span className="text-sm text-gray-700">
                      {new Date(barcode.validade).toLocaleDateString('pt-BR')}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono block overflow-x-auto max-w-[250px]">
                    {barcode.codigoCompleto}
                  </code>
                </td>
                <td className="px-4 py-3">
                  {onRemove && (
                    <button
                      onClick={() => onRemove(index)}
                      className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700 transition-colors"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview of export format */}
      <div className="border-t bg-gray-50 px-4 py-3">
        <p className="text-xs text-gray-500 mb-2">Preview do arquivo TXT:</p>
        <pre className="bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto">
          {generateExportContent()}
        </pre>
      </div>
    </div>
  );
};
